import { useState } from "react";
import { CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { InfoPopover } from "@/components/cinch/InfoPopover";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import {
  TableCell,
  TableRow,
} from "@/components/ui/table";

import {
  fmtMoney,
  fmtPct,
  fmtQty,
  fmtRate,
  monthLabel,
  useCosOutputs,
  useCosPeriod,
  useOpenCosPeriod,
  useRecompute,
  useReopenCosPeriod,
  useSetRecordMode,
  useSignOffCosPeriod,
  type LineOutputRead,
  type PeriodDetail,
} from "../api";
import { LineItemsDrawer } from "./LineItemsDrawer";

/**
 * The month's Simple Summary equivalent: one row per product line with total,
 * units, $/metric, full-capacity $/metric, and utilization. Clicking a row
 * opens the quick-view drawer with the invoice lines behind the number; the
 * drawer expands to full width for the whole detail.
 */
export function DetailsView({ period, canWrite }: { period: string; canWrite: boolean }) {
  const { data: detail, isLoading: detailLoading } = useCosPeriod(period);
  const exists = detail?.exists ?? false;
  const outputs = useCosOutputs(period, exists);
  const recompute = useRecompute(period);
  const [drawerLine, setDrawerLine] = useState<LineOutputRead | null>(null);
  const [expanded, setExpanded] = useState(false);

  const lines = outputs.data?.lines ?? [];
  const hasAnyCost = lines.some((ln) => (ln.total_expense ?? 0) !== 0);

  const columns: DataTableColumn<LineOutputRead>[] = [
    {
      key: "title",
      header: "Product line",
      render: (ln) => (
        <div>
          <div className="font-medium">{ln.title}</div>
          <div className="text-xs text-muted-foreground">{ln.metric_label}</div>
        </div>
      ),
    },
    {
      key: "total",
      header: "Total expense",
      align: "right",
      numeric: true,
      render: (ln) => (
        <div>
          <div>{fmtMoney(ln.total_expense)}</div>
          {ln.workbook_total != null &&
            ln.total_expense != null &&
            Math.abs(ln.workbook_total - ln.total_expense) > 0.5 && (
              <div className="text-[11px] text-warning">
                workbook: {fmtMoney(ln.workbook_total)}
              </div>
            )}
        </div>
      ),
    },
    {
      key: "units",
      header: "Metric qty",
      align: "right",
      numeric: true,
      render: (ln) => (
        <div>
          <div>{fmtQty(ln.metric_units)}</div>
          {ln.metric_source != null && (
            <div className="text-[11px] text-muted-foreground">{ln.metric_source}</div>
          )}
        </div>
      ),
    },
    {
      key: "per_metric",
      header: "$/Metric",
      align: "right",
      numeric: true,
      render: (ln) => fmtRate(ln.dollar_per_metric_actual),
    },
    {
      key: "fc_excl",
      header: "Full cap excl tax",
      align: "right",
      numeric: true,
      render: (ln) => fmtRate(ln.full_cap_per_metric_excl),
    },
    {
      key: "fc_incl",
      header: "Full cap incl tax",
      align: "right",
      numeric: true,
      render: (ln) => fmtRate(ln.full_cap_per_metric_incl),
    },
    {
      key: "utilization",
      header: "Utilization",
      align: "right",
      render: (ln) => fmtPct(ln.utilization),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <CloseBanner period={period} data={detail ?? null} loading={detailLoading} />

      {exists && (outputs.data?.unmapped_rows ?? 0) > 0 && (
        <Alert variant="warning">
          <AlertTitle>
            {outputs.data?.unmapped_rows} line item
            {outputs.data?.unmapped_rows === 1 ? "" : "s"} still unmapped
          </AlertTitle>
          <AlertDescription>
            Unmapped invoice lines are not counted in any product line&apos;s total
            {canWrite ? " — classify them on the Mappings tab." : "."}
          </AlertDescription>
        </Alert>
      )}

      {exists && (
        <DataTable
          columns={columns}
          rows={lines}
          rowKey={(ln) => ln.product_line_key}
          loading={outputs.isLoading}
          error={
            outputs.error != null
              ? outputs.error instanceof Error
                ? outputs.error.message
                : String(outputs.error)
              : null
          }
          emptyMessage="Nothing computed for this month yet — upload its cost files."
          onRowClick={(ln) => {
            setDrawerLine(ln);
            setExpanded(false);
          }}
          selectedKey={drawerLine?.product_line_key ?? null}
          toolbar={
            <div className="flex items-center gap-3">
              {outputs.data?.computed_at && (
                <span className="text-xs text-muted-foreground">
                  computed {new Date(outputs.data.computed_at).toLocaleString()}
                  {outputs.data.status === "signed_off" ? " (snapshot)" : ""}
                </span>
              )}
              {canWrite && detail?.status !== "signed_off" && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={recompute.isPending}
                  onClick={() =>
                    recompute.mutate(undefined, {
                      onError: (e) =>
                        toast.error("Recompute failed", { description: (e as Error).message }),
                    })
                  }
                >
                  <RefreshCw
                    className={cn("mr-1.5 h-3.5 w-3.5", recompute.isPending && "animate-spin")}
                  />
                  Recompute
                </Button>
              )}
            </div>
          }
          footer={(cols) =>
            hasAnyCost ? (
              <TableRow>
                <TableCell className="font-semibold">TOTAL</TableCell>
                <TableCell className="text-right font-semibold tabular-nums">
                  {fmtMoney(outputs.data?.grand_total ?? null)}
                </TableCell>
                {cols.slice(2).map((c) => (
                  <TableCell key={c.key} />
                ))}
              </TableRow>
            ) : null
          }
        />
      )}

      <LineItemsDrawer
        period={period}
        line={drawerLine}
        open={drawerLine != null}
        onOpenChange={(open) => {
          if (!open) {
            setDrawerLine(null);
            setExpanded(false);
          }
        }}
        expanded={expanded}
        onExpandedChange={setExpanded}
      />
    </div>
  );
}

function CloseBanner({
  period,
  data,
  loading,
}: {
  period: string;
  data: PeriodDetail | null;
  loading: boolean;
}) {
  const open = useOpenCosPeriod(period);
  const signOff = useSignOffCosPeriod(period);
  const reopen = useReopenCosPeriod(period);
  const recordMode = useSetRecordMode(period);

  if (loading && !data) return <Card className="h-[92px] animate-pulse" />;

  const status = data?.status ?? "not_started";
  const signedOff = status === "signed_off";

  return (
    <Card className="flex flex-wrap items-center justify-between gap-4 p-5">
      <div className="flex items-center gap-4">
        <div
          className={cn(
            "flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full",
            signedOff
              ? "bg-success-light text-success"
              : status === "not_started"
                ? "bg-neutral-light text-muted-foreground"
                : "bg-warning-light text-warning",
          )}
        >
          {signedOff ? <CheckCircle2 className="h-6 w-6" /> : <Loader2 className="h-6 w-6" />}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold">{monthLabel(period)} cost report</span>
            <StatusBadge value={status} />
            {data?.use_workbook_totals && (
              <span className="inline-flex items-center gap-1">
                <Badge variant="outline">workbook is the record</Badge>
                <InfoPopover title="Legacy record mode">
                  Line totals come from the uploaded workbook&apos;s Simple Summary, not
                  the engine&apos;s line-item computation — for the early months where
                  the spreadsheet is the answer of record.
                </InfoPopover>
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {signedOff
              ? `Signed off by ${data?.signed_off_by_name ?? "—"}${
                  data?.signed_off_at
                    ? ` on ${new Date(data.signed_off_at).toLocaleDateString()}`
                    : ""
                }. The numbers below are the frozen snapshot.`
              : status === "not_started"
                ? "Not opened yet. Open the month (or upload its files) to start the cost close."
                : "In progress — upload the cost files and map their line items."}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {!data?.exists && data?.can_open && (
          <Button
            onClick={() =>
              open.mutate(undefined, {
                onSuccess: () => toast.success(`${monthLabel(period)} opened`),
                onError: (e) =>
                  toast.error("Couldn't open month", { description: (e as Error).message }),
              })
            }
            disabled={open.isPending}
          >
            {open.isPending ? "Opening…" : "Open month"}
          </Button>
        )}
        {status === "in_progress" && data?.can_sign_off && (
          <Button
            onClick={() =>
              signOff.mutate(undefined, {
                onSuccess: () => toast.success("Cost report signed off"),
                onError: (e) =>
                  toast.error("Couldn't sign off", { description: (e as Error).message }),
              })
            }
            disabled={signOff.isPending}
          >
            <CheckCircle2 className="mr-1.5 h-4 w-4" />
            {signOff.isPending ? "Signing off…" : "Sign off"}
          </Button>
        )}
        {status === "in_progress" && data?.can_write && data?.exists && (
          <Button
            variant="outline"
            disabled={recordMode.isPending}
            onClick={() =>
              recordMode.mutate(!data.use_workbook_totals, {
                onSuccess: () =>
                  toast.success(
                    data.use_workbook_totals
                      ? "Back to engine-computed totals"
                      : "Workbook totals are now the record for this month",
                  ),
                onError: (e) =>
                  toast.error("Couldn't switch record mode", {
                    description: (e as Error).message,
                  }),
              })
            }
          >
            {data.use_workbook_totals ? "Use computed totals" : "Use workbook totals"}
          </Button>
        )}
        {signedOff && data?.can_sign_off && (
          <Button
            variant="outline"
            onClick={() =>
              reopen.mutate(undefined, {
                onSuccess: () => toast.success("Cost report reopened"),
                onError: (e) =>
                  toast.error("Couldn't reopen", { description: (e as Error).message }),
              })
            }
            disabled={reopen.isPending}
          >
            {reopen.isPending ? "Reopening…" : "Reopen"}
          </Button>
        )}
      </div>
    </Card>
  );
}
